import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { isTeacherSide, type ConversationSummary } from "@edu/shared";
import { ApiError } from "../../lib/api.ts";
import { LoadingRows, LoadFailed } from "../../lib/loading.tsx";
import { Empty } from "../../lib/Empty.tsx";
import { useSession } from "../../lib/session.tsx";
import { agoLabel } from "../../lib/when.ts";
import { useConversationNewsValue } from "../shell/ConversationNews.tsx";
import { announcementErrorText, conversationsTailText, previewText } from "./announcement-text.ts";
import { listConversations } from "./conversations-api.ts";

/**
 * Список разговоров: с кем, что написали последним, когда и сколько нового.
 *
 * ⚠️ **Списка два больше нет, и это главная правка 30.08.2026** (решение
 * владельца: «надо чтобы сообщения были в одном окне, а не каждое на своей
 * карточке»). Прежде экран делился на «Вы отправили» и «Вам написали», потому
 * что строкой был ОДИН пост, а у поста есть отправитель. Строка стала
 * РАЗГОВОРОМ, у разговора отправителя нет, и делить больше нечего: человек,
 * стоящий на обеих сторонах сразу (преподаватель, который сам у кого-то
 * учится), видит один список, где рядом лежат окна с его учениками и окно с его
 * преподавателем. Кто он в каждом, видно по содержимому окна.
 *
 * ⚠️ **Список это список, и примитив у него общий** (`docs/cabinet-tables-design.md`):
 * `.list` со слотами фиксированной ширины. Своим видом обзавелось только само
 * окно переписки, потому что оно не список.
 *
 * ⚠️ **Экран перечитывает себя по опросу, а не по таймеру.** Таймер живёт один
 * на вкладку, в каркасе кабинета (`features/shell/ConversationNews.tsx`), и сюда
 * приезжает подписью `stamp`: изменилась хоть одна строка счётчика, значит пора
 * за списком. Свой таймер здесь означал бы два опроса вместо одного.
 *
 * ⚠️ **Ничто на этом экране не обещает доставку.** Транспорта уведомлений у
 * продукта нет (раздел 4 CLAUDE.md), сообщение доходит тогда, когда человек
 * зашёл в кабинет либо держит вкладку открытой.
 */
export function ConversationsPage() {
  const { state } = useSession();
  /**
   * Кнопка «Написать» стоит у стороны преподавателя, тем же правилом, что
   * разделы сайдбара: у человека без единой пары `teaching` ещё не выставлен, и
   * спрятать от него единственную дверь к рассылке значило бы спрятать фичу от
   * того, кто только завёл кабинет.
   */
  const teacher = state.status === "signed" && isTeacherSide(state.me);
  const news = useConversationNewsValue();

  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  /** Осталось ли что-то за списком. Считает сервер, см. `conversationsTailText`. */
  const [truncated, setTruncated] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const page = await listConversations();
      setItems(page.items);
      setTruncated(page.truncated);
      setErr(null);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? announcementErrorText(e.code, e.message)
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  }, []);

  // Первый заход и каждое изменение счётчика. `stamp` пустой до первого ответа
  // опроса, поэтому первый заход делает именно эта строка, а не отдельный эффект
  useEffect(() => {
    void reload();
  }, [reload, news.stamp]);

  // Одно «сейчас» на всю отрисовку: иначе соседние строки считают «сегодня» от
  // разных моментов, и на границе полуночи это разные ответы
  const now = Date.now();
  const tail = conversationsTailText(truncated);

  return (
    <main className="home home--list">
      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Сообщения</h1>
          {/* Счётчика у заголовка нет: «14 разговоров» это число, которое никто
              не держит в голове и ни на что не отвечает. Непрочитанное считает
              бейдж у пункта сайдбара, и второй раз тем же числом не говорится */}
          {teacher && (
            <span className="head__act">
              <Link className="btn btn--primary" to="/announcements/new">
                Написать
              </Link>
            </span>
          )}
        </div>
      </header>

      {/* Отказ и ожидание не показываются вместе, и у отказа есть повтор: один
          приём на все экраны переписки и групп (`lib/loading.tsx`) */}
      {err !== null && <LoadFailed text={err} onRetry={() => void reload()} />}
      {items === null && err === null && (
        <section className="card">
          <LoadingRows list="list--conv" cells={4} narrow="page" />
        </section>
      )}

      {items !== null && items.length === 0 && <ConversationsEmpty teacher={teacher} />}

      {items !== null && items.length > 0 && (
        <section className="card">
          <ul className="list list--conv" data-rows="page">
            <li className="list__head" aria-hidden="true">
              <span>С кем</span>
              <span>Последнее сообщение</span>
              <span>Когда</span>
              <span>Новых</span>
            </li>
            {items.map((c) => (
              <ConversationRow key={c.id} item={c} now={now} />
            ))}
          </ul>
        </section>
      )}

      {tail !== null && <p className="muted">{tail}</p>}
    </main>
  );
}

/**
 * Строка списка.
 *
 * ⚠️ **Слот занят всегда** (раздел 2 `docs/cabinet-tables-design.md`): нового
 * нет, стоит «0»; текста нет, стоит объяснение, куда он делся. Пустых ячеек в
 * списке не бывает, из-за них колонки соседних строк разъезжаются.
 *
 * ⚠️ **Имя автора приезжает готовым полем и своей подстановки здесь нет ни
 * одной.** Кто как подписан для этого читателя, решил сервер (`authorNameFor`), и
 * второе место, где решают, кто что видит, однажды поправят не так.
 */
function ConversationRow(props: { item: ConversationSummary; now: number }) {
  const c = props.item;
  const when = agoLabel(c.last.createdAt, props.now);

  /**
   * Кто написал последним, приставкой к тексту.
   *
   * Своё сообщение подписывается «Вы» у любого разговора: иначе строка «Приносим
   * словари» неотличима от такой же строки, которую написал собеседник. Чужое
   * подписывается только в ГРУППОВОМ окне: в личном имя автора это и есть
   * заголовок строки, и повторять его значит показывать пальцем на соседнюю
   * ячейку.
   */
  const who = c.last.mine ? "Вы" : c.kind === "group" ? c.last.authorName.trim() : "";

  return (
    <li className="row">
      {/* Имя строки это СОБЕСЕДНИК, а не начало сообщения. Разбор: `row__name`
          набран `--t-section` полужирным, и на фразе «Вы: Приносим словари» это
          читается как заголовок, которым она не является. Во всех остальных
          списках кабинета в этом слоте стоит название работы, урока или темы */}
      <span className="cell row__name" data-label="С кем">
        {c.title.trim() === "" ? "Без названия" : c.title}
      </span>

      <span className="cell">
        <Link to={`/announcements/${c.id}`}>
          {who === "" ? previewText(c.last) : `${who}: ${previewText(c.last)}`}
        </Link>
      </span>

      <span
        className="cell row__when"
        data-label="Когда"
        data-tone={when.tone}
        title={when.title}
      >
        {when.text}
      </span>

      <span className="cell row__num" data-label="Новых" data-quiet={c.unread === 0}>
        {c.unread}
      </span>
    </li>
  );
}

/**
 * Пусто.
 *
 * Ученику здесь нечего предложить: сообщения пишет преподаватель. Обещать, что о
 * новом сообщении его известят, нельзя: известить нечем.
 *
 * ⚠️ **«Вы ещё никому не писали» убрано 14.09.2026**, и это не перекраска.
 * Фраза считала в адрес преподавателя, то есть нарушала правило раздела 5.10
 * CLAUDE.md ровно в том виде, в каком оно там и записано: о списке она не
 * говорит ничего, а о человеке говорит, что он чего-то не сделал. Осталось
 * «Сообщений пока нет», факт про список.
 */
function ConversationsEmpty(props: { teacher: boolean }) {
  if (!props.teacher) {
    return (
      <Empty
        icon="chat"
        title="Сообщений пока нет"
        text="Здесь появятся сообщения от преподавателя."
      />
    );
  }
  return (
    <Empty
      icon="chat"
      title="Сообщений пока нет"
      text="Здесь будет переписка с учениками."
      action={{ label: "Написать", to: "/announcements/new" }}
    />
  );
}
